import { useMemo, useState } from 'react';
import { SimpleSelect } from '@tpct/admin-ui';
import SettingRow from '@/hub-shared/components/SettingRow';
import { __ } from '@/hub-shared/utils/i18n';
import type { RolePermissionMap, RoleTemplate } from '../types/role-templates';
import type { PermissionCatalogEntry, ReviewLevel } from './permissionCatalog';
import {
  PERMISSION_GROUPS_FOR_ROLE,
  REVIEW_LEVEL_OPTIONS,
  ROLE_DISPLAY_NAMES,
  applyBooleanEntry,
  applyReviewLevel,
  readBooleanEntry,
  readReviewLevel,
} from './permissionCatalog';
import type { PortalBootstrapData } from '../types/window';
import GateNotice from '../gates/GateNotice';
import type { PortalGateDecision } from '../gates/portalGates';
import { parseApiError } from '../utils/parseApiError';
import { errorToUserFacingMessage } from '../api/apiErrors';

const BOOL_OPTIONS = () => [
  { value: 'yes', label: __('Oui') },
  { value: 'no', label: __('Non') },
];

type RolePermissionEditorProps = {
  template: RoleTemplate;
  bootstrap: PortalBootstrapData;
  permissionGate: PortalGateDecision;
  onBack: () => void;
  onSaved: (updated: RoleTemplate) => void;
};

type SaveState = 'idle' | 'saving' | 'error';

const isOwnershipTransferEntry = (entry: PermissionCatalogEntry): boolean =>
  entry.uiKey === 'ownership_transfer';

const isReviewLevelValue = (value: string): value is ReviewLevel =>
  value === 'none' || value === 'review' || value === 'supervise';

const isBooleanSelectValue = (value: string): value is 'yes' | 'no' =>
  value === 'yes' || value === 'no';

const isEntryDisabled = (entry: PermissionCatalogEntry, template: RoleTemplate): boolean =>
  template.read_only || isOwnershipTransferEntry(entry);

type PermissionRowProps = {
  entry: PermissionCatalogEntry;
  permissions: RolePermissionMap;
  disabled: boolean;
  onChange: (updated: RolePermissionMap) => void;
};

const PermissionRow = ({ entry, permissions, disabled, onChange }: PermissionRowProps) => {
  if (entry.type === 'review_level') {
    return (
      <SettingRow label={entry.label} description={entry.description} disabled={disabled}>
        <SimpleSelect
          options={REVIEW_LEVEL_OPTIONS()}
          value={readReviewLevel(permissions)}
          disabled={disabled}
          onChange={(v) => {
            if (isReviewLevelValue(v)) {
              onChange(applyReviewLevel(permissions, v));
            }
          }}
        />
      </SettingRow>
    );
  }

  if (entry.type !== 'boolean') {
    return null;
  }

  return (
    <SettingRow label={entry.label} description={entry.description} disabled={disabled}>
      <SimpleSelect
        options={BOOL_OPTIONS()}
        value={readBooleanEntry(entry, permissions) ? 'yes' : 'no'}
        disabled={disabled}
        onChange={(v) => {
          if (isBooleanSelectValue(v)) {
            onChange(applyBooleanEntry(entry, permissions, v === 'yes'));
          }
        }}
      />
    </SettingRow>
  );
};

const RolePermissionEditor = ({
  template,
  bootstrap,
  permissionGate,
  onBack,
  onSaved,
}: RolePermissionEditorProps) => {
  const [permissions, setPermissions] = useState<RolePermissionMap>(() => ({ ...template.permissions }));
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveError, setSaveError] = useState('');

  const groups = useMemo(() => PERMISSION_GROUPS_FOR_ROLE(template.role_key), [template.role_key]);
  const roleDisplayNames = useMemo(() => ROLE_DISPLAY_NAMES(), []);
  const roleName = roleDisplayNames[template.role_key] ?? template.role_key;
  const gateDisabled = !permissionGate.allowed;

  const isDirty = useMemo(() => {
    const orig = template.permissions;
    for (const key of Object.keys(permissions)) {
      if (permissions[key] !== orig[key]) return true;
    }
    return false;
  }, [permissions, template.permissions]);

  const handleSave = async () => {
    setSaveState('saving');
    setSaveError('');

    try {
      const response = await fetch(
        `${bootstrap.restUrls.accountingHub}user-management/role-templates/${template.role_key}`,
        {
          method: 'PUT',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': bootstrap.nonce,
          },
          body: JSON.stringify({ permissions }),
        },
      );

      if (!response.ok) {
        throw new Error(await parseApiError(response));
      }

      setSaveState('idle');
      onSaved({ ...template, permissions });
    } catch (e) {
      setSaveError(errorToUserFacingMessage(e, __('Une erreur est survenue.')));
      setSaveState('error');
    }
  };

  return (
    <div className={`role-permission-editor container-style${template.read_only ? ' role-permission-editor--read-only' : ''}`}>
      <h3 className="role-permission-editor__title">
        {__('Modifier le rôle : ') + roleName}
      </h3>

      <GateNotice decision={permissionGate} />

      {saveState === 'error' ? (
        <span className="role-permission-editor__error">{saveError}</span>
      ) : null}

      {groups.map((group) => (
        <section key={group.groupKey} className="role-permission-editor__group">
          <h4 className="role-permission-editor__group-title">{group.label}</h4>
          {group.entries.map((entry) => (
            <PermissionRow
              key={entry.uiKey}
              entry={entry}
              permissions={permissions}
              disabled={gateDisabled || isEntryDisabled(entry, template)}
              onChange={setPermissions}
            />
          ))}
        </section>
      ))}

      <div className="role-permission-editor__actions">
        <button type="button" className="btn-secondary" onClick={onBack} disabled={saveState === 'saving'}>
          {__('Annuler')}
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={() => { void handleSave(); }}
          disabled={gateDisabled || template.read_only || !isDirty || saveState === 'saving'}
        >
          {saveState === 'saving' ? __('Enregistrement…') : __('Enregistrer')}
        </button>
      </div>
    </div>
  );
};

export default RolePermissionEditor;
